import { useEffect, type Dispatch, type MutableRefObject, type SetStateAction } from "react"
import type { Scene3dEngine } from "@/lib/location-scene-3d/engine"
import type { PlatformLayerState } from "@/lib/location-scene-3d/platform-layer"
import { normalizeSceneLayout, sceneFacesFingerprint, type SceneLayoutState } from "@/lib/location-scene-3d/scene-state"
import { fetchScene3d } from "@/lib/library-api"

type UseLocationSceneFacePollArgs = {
  hasPendingFaces: boolean
  projectId: number
  scene3dId: number | null
  boundScene3dIdRef: MutableRefObject<number | null>
  engineRef: MutableRefObject<Scene3dEngine | null>
  platformVisualRef: MutableRefObject<PlatformLayerState | null>
  platformLayoutRef: MutableRefObject<SceneLayoutState | null>
  setPlatformLayout: Dispatch<SetStateAction<SceneLayoutState | null>>
}

/** Poll Scene3d while any face material is pending — apply faces only (keep local terrain). */
export function useLocationSceneFacePoll({
  hasPendingFaces,
  projectId,
  scene3dId,
  boundScene3dIdRef,
  engineRef,
  platformVisualRef,
  platformLayoutRef,
  setPlatformLayout,
}: UseLocationSceneFacePollArgs) {
  useEffect(() => {
    if (!hasPendingFaces) return
    let cancelled = false
    let generation = 0

    const timer = window.setInterval(() => {
      const id = boundScene3dIdRef.current ?? scene3dId
      if (id == null) return
      const requestId = ++generation
      void fetchScene3d(projectId, id)
        .then((detail) => {
          if (cancelled || requestId !== generation) return
          if ((boundScene3dIdRef.current ?? scene3dId) !== id) return
          const next = normalizeSceneLayout(detail.scene_state)
          const faces = next.faces ?? []
          const prevFp = sceneFacesFingerprint(platformLayoutRef.current?.faces)
          const nextFp = sceneFacesFingerprint(faces)
          if (prevFp === nextFp) return
          setPlatformLayout((prev) => (prev ? { ...prev, faces } : { ...next, faces }))
          const engine = engineRef.current
          const prev = platformVisualRef.current
          if (!engine || !prev) return
          const merged = { ...prev, faces }
          platformVisualRef.current = merged
          engine.setPlatformState(merged)
        })
        .catch(() => undefined)
    }, 2000)

    return () => {
      cancelled = true
      window.clearInterval(timer)
    }
  }, [
    boundScene3dIdRef,
    engineRef,
    hasPendingFaces,
    platformLayoutRef,
    platformVisualRef,
    projectId,
    scene3dId,
    setPlatformLayout,
  ])
}
